import { useCallback, useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import ProtectedRoute from "../../components/ProtectedRoute";
import { AuthProvider } from "../../context/AuthContext";
import Header from "../../components/admin/Header";
import Sidebar from "../../components/admin/Sidebar";

function useTheme() {
  const [theme, setThemeState] = useState<"light" | "dark">("light");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const storedTheme =
      (localStorage.getItem("theme") as "light" | "dark") || "light";
    setThemeState(storedTheme);
    setMounted(true);
  }, []);

  const setTheme = useCallback((newTheme: "light" | "dark") => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);
  }, []);

  useEffect(() => {
    if (mounted) {
      document.documentElement.classList.toggle("dark", theme === "dark");
    }
  }, [theme, mounted]);

  return { theme, setTheme, mounted };
}

function ManageReportContent() {
  const { theme, setTheme } = useTheme();
  const { logout, token: authToken } = useAuth();

  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingDummy, setUsingDummy] = useState(false); // Flag for dummy fallback

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";

  // Dummy data for reports
  const dummyReports = [
    {
      id: "REP-001",
      type: "User Summary",
      generatedAt: "2025-11-30T10:00:00Z",
      status: "completed",
    },
    {
      id: "REP-002",
      type: "Activity Log",
      generatedAt: "2025-11-29T15:30:00Z",
      status: "processing",
    },
    {
      id: "REP-003",
      type: "Performance Metrics",
      generatedAt: "2025-11-28T09:15:00Z",
      status: "completed",
    },
  ];

  // Fetch reports - assuming an endpoint /reports
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setUsingDummy(false);

      if (!authToken) {
        throw new Error("No authentication token available");
      }

      const response = await fetch(`${API_BASE_URL}/reports`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        // Check if 404 (endpoint not found) - use dummy
        if (response.status === 404) {
          setReports(dummyReports);
          setUsingDummy(true);
          return; // Don't set error for 404
        }
        throw new Error("Failed to fetch reports");
      }

      const data = await response.json();
      const fetchedReports = data.data || [];
      setReports(fetchedReports.length > 0 ? fetchedReports : dummyReports); // Dummy if empty
    } catch (err) {
      // Non-404 errors (e.g., network, auth) show error
      if (err instanceof Error && !err.message.includes("404")) {
        setError(err.message || "Failed to load reports");
      } else {
        // 404 or unknown - fallback to dummy without error
        setReports(dummyReports);
        setUsingDummy(true);
      }
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, authToken]);

  // Generate report - assuming POST /reports/generate
  const generateReport = useCallback(async () => {
    try {
      if (!authToken) throw new Error("No authentication token available");

      const response = await fetch(`${API_BASE_URL}/reports/generate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ type: "all" }), // Example payload
      });

      if (!response.ok) {
        // Handle 404 gracefully if endpoint doesn't exist
        if (response.status === 404) {
          alert("Report generation endpoint not implemented yet. Using dummy data.");
          return;
        }
        throw new Error("Failed to generate report");
      }

      await fetchReports();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate report");
    }
  }, [API_BASE_URL, authToken, fetchReports]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Use dummy if no real reports or error (non-critical)
  const displayReports = reports.length > 0 ? reports : dummyReports;

  if (loading)
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 dark:border-gray-400"></div>
      </div>
    );

  // Only show critical errors (non-404)
  if (error && !usingDummy)
    return (
      <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded-md border border-red-200 dark:border-red-800">
        <p className="text-red-700 dark:text-red-300">{error}</p>
        <button
          onClick={fetchReports}
          className="mt-3 bg-red-200 dark:bg-red-800 px-3 py-1 rounded text-red-700 dark:text-red-200"
        >
          Try Again
        </button>
      </div>
    );

  // Optional: Show a subtle notice if using dummy
  const showDummyNotice = usingDummy || displayReports === dummyReports;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
      <Header theme={theme} setTheme={setTheme} logout={logout} />

      <div className="flex">
        <Sidebar onManageUsers={() => {}} onGenerateReport={() => {}} />

        <main className="flex-1 lg:ml-64">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Manage Reports</h1>
              <button
                onClick={generateReport}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Generate New Report
              </button>
            </div>

            {/* Optional dummy notice */}
            {showDummyNotice && (
              <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md text-yellow-800 dark:text-yellow-200 text-sm">
                Using sample data (API endpoint not available yet).
              </div>
            )}

            {/* Reports Table */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Report ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Generated At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {displayReports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{report.id}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{report.type}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{new Date(report.generatedAt).toLocaleString()}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          report.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          {report.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium">
                        <button className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300">
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {displayReports.length === 0 && (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  No reports found. Generate a new report to get started.
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default function ManageReport() {
  return (
    <AuthProvider>
      <ProtectedRoute allowedRoles={["ADMIN"]}>
        <ManageReportContent />
      </ProtectedRoute>
    </AuthProvider>
  );
}